import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { 
  Church, Users, UserCheck, Calendar, Eye, 
  ChevronDown, ChevronUp, Loader2, LogOut,
  Building2, MapPin, ArrowRight, X, Search,
  Download, FileSpreadsheet, UserCog, ArrowLeftRight,
  Mail, MessageSquare, Phone, Trash2, CheckCircle,
  Clock, ShieldAlert, ChevronLeft, ChevronRight, Filter,
  DatabaseBackup, Upload
} from 'lucide-react';
import { toast } from 'sonner';

// Backend URL - Use environment variable in production
const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : (window.location.origin.includes('localhost')
    ? 'http://localhost:8001/api'
    : `${window.location.origin}/api`);

const BishopDashboard = ({ user, token, onLogout, onImpersonate }) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedChurch, setSelectedChurch] = useState(null);
  const [churchData, setChurchData] = useState(null);
  const [churchAssignments, setChurchAssignments] = useState(null);
  const [churchDeacons, setChurchDeacons] = useState(null);
  const [expandedChurch, setExpandedChurch] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [viewMode, setViewMode] = useState('summary'); // summary, assignments, deacons
  const [dateFilter, setDateFilter] = useState('');
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [exportingReport, setExportingReport] = useState(false);
  
  // Super Admin User Management States
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [editingUserRole, setEditingUserRole] = useState(null);
  const [newRoleValue, setNewRoleValue] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [expandedUserId, setExpandedUserId] = useState(null);
  
  // Add User States
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    full_name: '',
    email: '',
    role: 'user',
    church_id: ''
  });
  const [addingUser, setAddingUser] = useState(false);
  
  // Change Church States
  const [changingChurchUser, setChangingChurchUser] = useState(null);
  const [newChurchId, setNewChurchId] = useState('');
  
  // Contact Messages States
  const [contactMessages, setContactMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Login Logs States
  const [loginLogs, setLoginLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  const [logsFilter, setLogsFilter] = useState('all');

  // Migration States
  const [migrationExporting, setMigrationExporting] = useState(false);
  const [migrationImporting, setMigrationImporting] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchDashboard();
    if (isSuperAdmin) {
      fetchAllUsers();
      fetchContactMessages();
      fetchLoginLogs();
    }
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/bishop/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // Super Admin - Fetch all users in the system
  const fetchAllUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API}/superadmin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching all users:', error);
      toast.error('خطأ في تحميل المستخدمين');
    } finally {
      setLoadingUsers(false);
    }
  };

  // Super Admin - Change user role
  const handleChangeUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/superadmin/users/${userId}/role?new_role=${newRole}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('تم تغيير دور المستخدم بنجاح');
      fetchAllUsers();
      setEditingUserRole(null);
    } catch (error) {
      console.error('Error changing user role:', error);
      toast.error(error.response?.data?.detail || 'خطأ في تغيير الدور');
    }
  };

  // Super Admin - Add new user
  const handleAddUser = async () => {
    if (!newUser.username || !newUser.password || !newUser.church_id) {
      toast.error('يرجى ملء جميع الحقول المطلوبة');
      return;
    }
    if (newUser.password.length < 6) {
      toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }
    
    setAddingUser(true);
    try {
      await axios.post(`${API}/superadmin/users/create`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('تم إضافة المستخدم بنجاح');
      fetchAllUsers();
      setShowAddUserForm(false);
      setNewUser({
        username: '',
        password: '',
        full_name: '',
        email: '',
        role: 'user',
        church_id: ''
      });
    } catch (error) {
      console.error('Error adding user:', error);
      toast.error(error.response?.data?.detail || 'خطأ في إضافة المستخدم');
    } finally {
      setAddingUser(false);
    }
  };

  // Super Admin - Change user church
  const handleChangeUserChurch = async (userId, newChurchId) => {
    try {
      await axios.put(`${API}/superadmin/users/${userId}/church?new_church_id=${newChurchId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('تم تغيير كنيسة المستخدم بنجاح');
      fetchAllUsers();
      setChangingChurchUser(null);
      setNewChurchId('');
    } catch (error) {
      console.error('Error changing user church:', error);
      toast.error(error.response?.data?.detail || 'خطأ في تغيير الكنيسة');
    }
  };

  // Super Admin - Delete any user
  const handleDeleteAnyUser = async (userId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المستخدم؟')) return;
    
    try {
      await axios.delete(`${API}/superadmin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('تم حذف المستخدم بنجاح');
      fetchAllUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'خطأ في حذف المستخدم');
    }
  };

  // Super Admin - Toggle user status
  const handleToggleUserStatus = async (userId) => {
    try {
      const response = await axios.put(`${API}/superadmin/users/${userId}/status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchAllUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error(error.response?.data?.detail || 'خطأ في تغيير حالة المستخدم');
    }
  };

  // Super Admin - Reset user password
  const handleResetUserPassword = async (userId) => {
    const newPassword = prompt('أدخل كلمة المرور الجديدة (6 أحرف على الأقل):');
    if (!newPassword || newPassword.length < 6) {
      toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }
    
    try {
      await axios.post(`${API}/superadmin/users/${userId}/reset-password?new_password=${encodeURIComponent(newPassword)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('تم إعادة تعيين كلمة المرور بنجاح');
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(error.response?.data?.detail || 'خطأ في إعادة تعيين كلمة المرور');
    }
  };

  // Filter users based on search and role
  const filteredUsers = allUsers.filter(u => {
    const matchesSearch = !userSearchQuery || 
      u.username?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
      u.full_name?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
      u.church_name?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(userSearchQuery.toLowerCase());
    const matchesRole = userRoleFilter === 'all' || u.role === userRoleFilter || (userRoleFilter === 'admin' && (u.role === 'admin' || u.role === 'church_admin'));
    return matchesSearch && matchesRole;
  });

  // Super Admin - Fetch contact messages
  const fetchContactMessages = async () => {
    setLoadingMessages(true);
    try {
      const response = await axios.get(`${API}/superadmin/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContactMessages(response.data.messages || []);
      setUnreadCount(response.data.unread || 0);
    } catch (error) {
      console.error('Error fetching contact messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Mark message as read
  const handleMarkMessageRead = async (messageId) => {
    try {
      await axios.put(`${API}/superadmin/messages/${messageId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchContactMessages();
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  // Delete message
  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الرسالة؟')) return;
    
    try {
      await axios.delete(`${API}/superadmin/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('تم حذف الرسالة');
      fetchContactMessages();
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('خطأ في حذف الرسالة');
    }
  };

  // Fetch login logs
  const fetchLoginLogs = async (page = 1, filter = logsFilter) => {
    setLoadingLogs(true);
    try {
      const filterParam = filter === 'all' ? '' : `&success_filter=${filter === 'success' ? 'true' : 'false'}`;
      const response = await axios.get(`${API}/superadmin/login-logs?page=${page}&limit=50${filterParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoginLogs(response.data.logs || []);
      setLogsTotalPages(response.data.pages || 1);
      setLogsPage(response.data.page || 1);
    } catch (error) {
      console.error('Error fetching login logs:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  // Export migration data
  const handleExportMigration = async () => {
    setMigrationExporting(true);
    try {
      const response = await axios.get(`${API}/admin/export-migration`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      link.setAttribute('download', `migration_export_${timestamp}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('تم تصدير البيانات بنجاح! يمكنك الآن استيرادها في بيئة الإنتاج.');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('خطأ في تصدير البيانات');
    } finally {
      setMigrationExporting(false);
    }
  };

  // Import migration data
  const handleImportMigration = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm('هل أنت متأكد من استيراد البيانات؟ سيتم تحديث البيانات الموجودة وإضافة البيانات الجديدة.')) {
      e.target.value = '';
      return;
    }
    setMigrationImporting(true);
    setMigrationResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/admin/import-migration`, formData, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000
      });
      setMigrationResult(response.data);
      toast.success('تم استيراد البيانات بنجاح!');
      // Refresh dashboard data
      fetchDashboard();
      fetchAllUsers();
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'خطأ في استيراد البيانات');
    } finally {
      setMigrationImporting(false);
      e.target.value = '';
    }
  };

  const fetchChurchDetails = async (churchId) => {
    setLoadingDetails(true);
    try {
      const response = await axios.get(`${API}/churches/${churchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChurchData(response.data);
      setSelectedChurch(churchId);
    } catch (error) {
      console.error('Error fetching church details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchChurchAssignments = async (churchId, date = null) => {
    setLoadingDetails(true);
    try {
      let url = `${API}/bishop/church/${churchId}/assignments`;
      if (date) {
        url += `?date=${date}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChurchAssignments(response.data);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchChurchDeacons = async (churchId) => {
    setLoadingDetails(true);
    try {
      const response = await axios.get(`${API}/bishop/church/${churchId}/deacons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChurchDeacons(response.data);
    } catch (error) {
      console.error('Error fetching deacons:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const openChurchDetails = (churchId, mode = 'summary') => {
    setSelectedChurch(churchId);
    setViewMode(mode);
    fetchChurchDetails(churchId);
    if (mode === 'assignments') {
      fetchChurchAssignments(churchId);
    } else if (mode === 'deacons') {
      fetchChurchDeacons(churchId);
    }
  };

  const closeChurchDetails = () => {
    setSelectedChurch(null);
    setChurchData(null);
    setChurchAssignments(null);
    setChurchDeacons(null);
    setViewMode('summary');
    setDateFilter('');
  };

  const handleDateFilter = () => {
    if (selectedChurch && dateFilter) {
      fetchChurchAssignments(selectedChurch, dateFilter);
    } else if (selectedChurch) {
      fetchChurchAssignments(selectedChurch);
    }
  };

  // Deactivate church (bishop & super_admin)
  const handleDeactivateChurch = async (churchId) => {
    setActionLoading(true);
    try {
      await axios.delete(`${API}/churches/${churchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchDashboard();
      alert('تم تعطيل الكنيسة بنجاح');
    } catch (error) {
      console.error('Error deactivating church:', error);
      alert('خطأ في تعطيل الكنيسة');
    } finally {
      setActionLoading(false);
      setShowDeleteConfirm(null);
    }
  };

  // Reactivate church
  const handleReactivateChurch = async (churchId) => {
    setActionLoading(true);
    try {
      await axios.put(`${API}/churches/${churchId}/reactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchDashboard();
      alert('تم إعادة تفعيل الكنيسة بنجاح');
    } catch (error) {
      console.error('Error reactivating church:', error);
      alert('خطأ في إعادة التفعيل');
    } finally {
      setActionLoading(false);
    }
  };

  // Permanently delete church (super_admin only)
  const handleDeleteChurchPermanently = async (churchId) => {
    if (!isSuperAdmin) {
      alert('صلاحيات المدير العام مطلوبة');
      return;
    }
    
    setActionLoading(true);
    try {
      await axios.delete(`${API}/churches/${churchId}/permanent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchDashboard();
      closeChurchDetails();
      alert('تم حذف الكنيسة نهائياً');
    } catch (error) {
      console.error('Error deleting church:', error);
      alert('خطأ في حذف الكنيسة');
    } finally {
      setActionLoading(false);
      setShowDeleteConfirm(null);
    }
  };

  // Delete deacon from church
  const handleDeleteDeacon = async (churchId, deaconId) => {
    setActionLoading(true);
    try {
      await axios.delete(`${API}/bishop/church/${churchId}/data/deacons/${deaconId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchChurchDeacons(churchId);
      fetchDashboard();
      alert('تم حذف الشماس بنجاح');
    } catch (error) {
      console.error('Error deleting deacon:', error);
      alert('خطأ في حذف الشماس');
    } finally {
      setActionLoading(false);
    }
  };

  // Impersonate church admin
  const handleImpersonateChurch = async (churchId, churchName) => {
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/bishop/impersonate/${churchId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Store impersonation info and switch to church admin mode
      if (onImpersonate) {
        onImpersonate(response.data.access_token, churchId, churchName);
      }
      
      toast.success(`تم الدخول ككنيسة: ${churchName}`);
    } catch (error) {
      console.error('Error impersonating church:', error);
      toast.error(error.response?.data?.detail || 'خطأ في الدخول للكنيسة');
    } finally {
      setActionLoading(false);
    }
  };

  // Export church report to Excel
  const handleExportChurchReport = async (churchId, churchName) => {
    setExportingReport(true);
    try {
      const response = await axios.get(
        `${API}/bishop/church/${churchId}/report/excel`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `تقرير_${churchName}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('تم تحميل التقرير بنجاح');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('خطأ في تصدير التقرير');
    } finally {
      setExportingReport(false);
    }
  };

  // Export all churches summary report
  const handleExportAllChurchesSummary = async () => {
    setExportingReport(true);
    try {
      const response = await axios.get(
        `${API}/bishop/summary-report/excel`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `تقرير_جميع_الكنائس_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('تم تحميل التقرير بنجاح');
    } catch (error) {
      console.error('Error exporting summary report:', error);
      toast.error('خطأ في تصدير التقرير');
    } finally {
      setExportingReport(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-indigo-100">
        <Loader2 className="w-12 h-12 animate-spin text-purple-600" />
      </div>
    );
  }

  // Church Details Modal
  const ChurchDetailsModal = () => {
    if (!selectedChurch || !churchData) return null;

    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <Church className="w-7 h-7" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">{churchData.name}</h2>
                  {churchData.location && (
                    <p className="text-purple-200 text-sm flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {churchData.location}
                    </p>
                  )}
                </div>
              </div>
              <Button
                onClick={closeChurchDetails}
                variant="ghost"
                className="text-white hover:bg-white/20"
              >
                <X className="w-6 h-6" />
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <p className="text-3xl font-bold">{churchData.deacons_count}</p>
                <p className="text-purple-200 text-sm">شماس</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <p className="text-3xl font-bold">{churchData.users_count}</p>
                <p className="text-purple-200 text-sm">مستخدم</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <p className="text-3xl font-bold">{churchData.assignments_count}</p>
                <p className="text-purple-200 text-sm">توزيع</p>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mt-6">
              <Button
                onClick={() => { setViewMode('summary'); }}
                variant={viewMode === 'summary' ? 'secondary' : 'ghost'}
                className={viewMode === 'summary' ? 'bg-white text-purple-700' : 'text-white hover:bg-white/20'}
              >
                ملخص
              </Button>
              <Button
                onClick={() => { setViewMode('assignments'); fetchChurchAssignments(selectedChurch); }}
                variant={viewMode === 'assignments' ? 'secondary' : 'ghost'}
                className={viewMode === 'assignments' ? 'bg-white text-purple-700' : 'text-white hover:bg-white/20'}
              >
                التوزيعات
              </Button>
              <Button
                onClick={() => { setViewMode('deacons'); fetchChurchDeacons(selectedChurch); }}
                variant={viewMode === 'deacons' ? 'secondary' : 'ghost'}
                className={viewMode === 'deacons' ? 'bg-white text-purple-700' : 'text-white hover:bg-white/20'}
              >
                الشمامسة
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[50vh]">
            {loadingDetails ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
              </div>
            ) : (
              <>
                {/* Summary View */}
                {viewMode === 'summary' && churchData.recent_assignments && (
                  <div>
                    <h3 className="font-bold text-gray-800 mb-4">آخر التوزيعات</h3>
                    {churchData.recent_assignments.length > 0 ? (
                      <div className="space-y-2">
                        {churchData.recent_assignments.map((assignment, idx) => (
                          <div key={idx} className="bg-gray-50 p-3 rounded-lg flex justify-between items-center">
                            <span className="font-medium">{assignment.deacon_name}</span>
                            <span className="text-purple-600">{assignment.reading_type}</span>
                            <span className="text-gray-500 text-sm">{assignment.date}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">لا توجد توزيعات</p>
                    )}
                  </div>
                )}

                {/* Assignments View */}
                {viewMode === 'assignments' && churchAssignments && (
                  <div>
                    {/* Date Filter */}
                    <div className="flex gap-2 mb-4">
                      <Input
                        type="date"
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        className="max-w-xs"
                      />
                      <Button onClick={handleDateFilter} variant="outline">
                        <Search className="w-4 h-4 ml-1" />
                        بحث
                      </Button>
                      {dateFilter && (
                        <Button onClick={() => { setDateFilter(''); fetchChurchAssignments(selectedChurch); }} variant="ghost">
                          عرض الكل
                        </Button>
                      )}
                    </div>

                    <p className="text-gray-600 mb-4">
                      إجمالي: {churchAssignments.total_count} توزيع في {churchAssignments.dates_count} يوم
                    </p>

                    {/* Assignments grouped by date */}
                    {churchAssignments.assignments_by_date && Object.keys(churchAssignments.assignments_by_date).length > 0 ? (
                      <div className="space-y-4">
                        {Object.entries(churchAssignments.assignments_by_date)
                          .sort((a, b) => new Date(b[0]) - new Date(a[0]))
                          .slice(0, 20)
                          .map(([date, assignments]) => (
                            <div key={date} className="border rounded-lg overflow-hidden">
                              <div className="bg-purple-50 px-4 py-2 font-semibold text-purple-800">
                                {date} ({assignments.length} توزيع)
                              </div>
                              <div className="divide-y">
                                {assignments.map((assignment, idx) => (
                                  <div key={idx} className="px-4 py-2 flex justify-between items-center hover:bg-gray-50">
                                    <span className="font-medium">{assignment.deacon_name}</span>
                                    <span className="text-purple-600 text-sm bg-purple-100 px-2 py-1 rounded">
                                      {assignment.reading_type}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">لا توجد توزيعات</p>
                    )}
                  </div>
                )}

                {/* Deacons View */}
                {viewMode === 'deacons' && churchDeacons && (
                  <div>
                    <p className="text-gray-600 mb-4">إجمالي: {churchDeacons.count} شماس</p>
                    
                    {churchDeacons.deacons && churchDeacons.deacons.length > 0 ? (
                      <div className="grid gap-3">
                        {churchDeacons.deacons.map((deacon, idx) => (
                          <div key={idx} className="bg-gray-50 p-4 rounded-lg flex justify-between items-center">
                            <div>
                              <p className="font-semibold">{deacon.name}</p>
                              {deacon.phone && (
                                <p className="text-sm text-gray-500">{deacon.phone}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-left">
                                <span className="text-purple-600 font-bold">{deacon.assignments_count || 0}</span>
                                <span className="text-gray-500 text-sm mr-1">خدمة</span>
                              </div>
                              {isSuperAdmin && (
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => {
                                    if (confirm(`هل أنت متأكد من حذف الشماس "${deacon.name}"؟`)) {
                                      handleDeleteDeacon(selectedChurch, deacon.id);
                                    }
                                  }}
                                  disabled={actionLoading}
                                >
                                  حذف
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">لا يوجد شمامسة</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Delete Confirmation for Super Admin */}
          {isSuperAdmin && churchData && (
            <div className="border-t p-4 bg-red-50">
              <p className="text-red-600 text-sm mb-2">⚠️ إجراءات المدير العام:</p>
              <div className="flex gap-2">
                {churchData.is_active !== false ? (
                  <Button
                    variant="outline"
                    className="border-orange-500 text-orange-600 hover:bg-orange-50"
                    onClick={() => handleDeactivateChurch(selectedChurch)}
                    disabled={actionLoading}
                  >
                    تعطيل الكنيسة
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="border-green-500 text-green-600 hover:bg-green-50"
                    onClick={() => handleReactivateChurch(selectedChurch)}
                    disabled={actionLoading}
                  >
                    إعادة التفعيل
                  </Button>
                )}
                <Button
                  variant="destructive"
                  onClick={() => {
                    if (confirm(`هل أنت متأكد من حذف كنيسة "${churchData.name}" نهائياً؟ سيتم حذف كل البيانات!`)) {
                      handleDeleteChurchPermanently(selectedChurch);
                    }
                  }}
                  disabled={actionLoading}
                >
                  حذف نهائي
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-purple-100" dir="rtl">
      {/* Header */}
      <div className={`${isSuperAdmin ? 'bg-gradient-to-r from-red-800 to-red-900' : 'bg-gradient-to-r from-purple-800 to-indigo-900'} text-white shadow-lg`}>
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Church className="w-7 h-7" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">
                  {isSuperAdmin ? 'لوحة تحكم المدير العام' : 'لوحة تحكم نيافة الأسقف'}
                </h1>
                <p className={`${isSuperAdmin ? 'text-red-200' : 'text-purple-200'} text-sm`}>
                  {user?.full_name || (isSuperAdmin ? 'المدير العام' : 'نيافة الأنبا')}
                  {isSuperAdmin && <span className="mr-2 bg-red-500 px-2 py-0.5 rounded text-xs">صلاحيات كاملة</span>}
                </p>
              </div>
            </div>
            <Button
              onClick={onLogout}
              variant="ghost"
              className="text-white hover:bg-white/20"
            >
              <LogOut className="w-5 h-5 ml-2" />
              تسجيل الخروج
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">إجمالي الكنائس</p>
                  <p className="text-4xl font-bold mt-1">{dashboard?.total_churches || 0}</p>
                </div>
                <Building2 className="w-12 h-12 text-purple-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">إجمالي الشمامسة</p>
                  <p className="text-4xl font-bold mt-1">{dashboard?.total_deacons || 0}</p>
                </div>
                <Users className="w-12 h-12 text-blue-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">إجمالي المستخدمين</p>
                  <p className="text-4xl font-bold mt-1">{dashboard?.total_users || 0}</p>
                </div>
                <UserCheck className="w-12 h-12 text-green-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm">إجمالي التوزيعات</p>
                  <p className="text-4xl font-bold mt-1">{dashboard?.total_assignments || 0}</p>
                </div>
                <Calendar className="w-12 h-12 text-amber-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Churches List */}
        <Card className="shadow-lg">
          <CardHeader className="bg-gradient-to-r from-purple-100 to-indigo-100">
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-purple-600" />
                الكنائس المسجلة ({dashboard?.churches?.filter(c => c.is_active !== false).length || 0})
                {isSuperAdmin && dashboard?.churches?.filter(c => c.is_active === false).length > 0 && (
                  <span className="text-sm text-red-500 font-normal">
                    ({dashboard?.churches?.filter(c => c.is_active === false).length} معطلة)
                  </span>
                )}
              </div>
              <Button
                onClick={handleExportAllChurchesSummary}
                disabled={exportingReport}
                className="bg-green-600 hover:bg-green-700 text-white"
                size="sm"
              >
                {exportingReport ? (
                  <Loader2 className="w-4 h-4 ml-1 animate-spin" />
                ) : (
                  <FileSpreadsheet className="w-4 h-4 ml-1" />
                )}
                تصدير تقرير شامل
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {/* Active Churches */}
              {dashboard?.churches?.filter(c => c.is_active !== false).map((church) => (
                <div key={church.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                        <Church className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{church.name}</h3>
                        {church.location && (
                          <p className="text-sm text-gray-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {church.location}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-center px-3">
                        <p className="text-xl font-bold text-purple-600">{church.deacons_count}</p>
                        <p className="text-xs text-gray-500">شماس</p>
                      </div>
                      <div className="text-center px-3">
                        <p className="text-xl font-bold text-amber-600">{church.assignments_count}</p>
                        <p className="text-xs text-gray-500">توزيع</p>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {/* Impersonate Button */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleImpersonateChurch(church.id, church.name)}
                          disabled={actionLoading}
                          className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                          title="الدخول كمدير للكنيسة"
                        >
                          <UserCog className="w-4 h-4 ml-1" />
                          إدارة
                        </Button>
                        {/* Export Report Button */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleExportChurchReport(church.id, church.name)}
                          disabled={exportingReport}
                          className="text-green-600 border-green-200 hover:bg-green-50"
                          title="تصدير تقرير الكنيسة"
                        >
                          <Download className="w-4 h-4 ml-1" />
                          تقرير
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openChurchDetails(church.id, 'assignments')}
                          className="text-amber-600 border-amber-200 hover:bg-amber-50"
                        >
                          <Calendar className="w-4 h-4 ml-1" />
                          التوزيعات
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openChurchDetails(church.id, 'deacons')}
                          className="text-purple-600 border-purple-200 hover:bg-purple-50"
                        >
                          <Users className="w-4 h-4 ml-1" />
                          الشمامسة
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => openChurchDetails(church.id, 'summary')}
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                        >
                          <Eye className="w-4 h-4 ml-1" />
                          التفاصيل
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Deactivated Churches (only visible to super_admin) */}
              {isSuperAdmin && dashboard?.churches?.filter(c => c.is_active === false).length > 0 && (
                <>
                  <div className="bg-red-50 px-4 py-2 text-red-700 font-semibold">
                    الكنائس المعطلة
                  </div>
                  {dashboard?.churches?.filter(c => c.is_active === false).map((church) => (
                    <div key={church.id} className="p-4 bg-red-50/50 hover:bg-red-100/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                            <Church className="w-6 h-6 text-red-400" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-600">{church.name}</h3>
                            <p className="text-sm text-red-500">معطلة</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReactivateChurch(church.id)}
                            className="border-green-500 text-green-600 hover:bg-green-50"
                            disabled={actionLoading}
                          >
                            إعادة التفعيل
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              if (confirm(`هل أنت متأكد من حذف "${church.name}" نهائياً؟`)) {
                                handleDeleteChurchPermanently(church.id);
                              }
                            }}
                            disabled={actionLoading}
                          >
                            حذف نهائي
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Super Admin - User Management Section */}
        {isSuperAdmin && (
          <Card data-testid="user-management-section" className="shadow-lg mt-8">
            <CardHeader className="bg-gradient-to-r from-red-100 to-orange-100">
              <CardTitle className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <UserCog className="w-5 h-5 text-red-600" />
                  إدارة جميع المستخدمين
                  <span className="text-sm text-gray-500 font-normal">
                    ({allUsers.length} مستخدم)
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid="add-user-btn"
                    onClick={() => setShowAddUserForm(!showAddUserForm)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    size="sm"
                  >
                    + إضافة مستخدم
                  </Button>
                  <Button
                    onClick={fetchAllUsers}
                    disabled={loadingUsers}
                    variant="outline"
                    size="sm"
                  >
                    {loadingUsers ? <Loader2 className="w-4 h-4 animate-spin" /> : 'تحديث'}
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {/* Add User Form */}
              {showAddUserForm && (
                <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-4">إضافة مستخدم جديد</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      data-testid="new-user-username"
                      placeholder="اسم المستخدم *"
                      value={newUser.username}
                      onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                    />
                    <Input
                      data-testid="new-user-password"
                      type="password"
                      placeholder="كلمة المرور *"
                      value={newUser.password}
                      onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    />
                    <Input
                      placeholder="الاسم الكامل"
                      value={newUser.full_name}
                      onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                    />
                    <Input
                      type="email"
                      placeholder="البريد الإلكتروني"
                      value={newUser.email}
                      onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    />
                    <select
                      data-testid="new-user-role"
                      value={newUser.role}
                      onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                      className="border rounded px-3 py-2"
                    >
                      <option value="user">مستخدم</option>
                      <option value="admin">مدير</option>
                      <option value="church_admin">مدير كنيسة</option>
                    </select>
                    <select
                      data-testid="new-user-church"
                      value={newUser.church_id}
                      onChange={(e) => setNewUser({...newUser, church_id: e.target.value})}
                      className="border rounded px-3 py-2"
                    >
                      <option value="">اختر الكنيسة *</option>
                      {dashboard?.churches?.map((church) => (
                        <option key={church.id} value={church.id}>
                          {church.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button
                      data-testid="submit-add-user"
                      onClick={handleAddUser}
                      disabled={addingUser}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {addingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : 'إضافة'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowAddUserForm(false);
                        setNewUser({ username: '', password: '', full_name: '', email: '', role: 'user', church_id: '' });
                      }}
                    >
                      إلغاء
                    </Button>
                  </div>
                </div>
              )}

              {/* Role Stats Summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                {[
                  { key: 'all', label: 'الكل', count: allUsers.length, color: 'bg-gray-100 text-gray-700 border-gray-300' },
                  { key: 'super_admin', label: 'مدير عام', count: allUsers.filter(u => u.role === 'super_admin').length, color: 'bg-red-50 text-red-700 border-red-300' },
                  { key: 'bishop', label: 'أسقف', count: allUsers.filter(u => u.role === 'bishop').length, color: 'bg-purple-50 text-purple-700 border-purple-300' },
                  { key: 'admin', label: 'مدير', count: allUsers.filter(u => u.role === 'admin' || u.role === 'church_admin').length, color: 'bg-amber-50 text-amber-700 border-amber-300' },
                  { key: 'user', label: 'مستخدم', count: allUsers.filter(u => u.role === 'user').length, color: 'bg-green-50 text-green-700 border-green-300' },
                ].map(stat => (
                  <button
                    key={stat.key}
                    data-testid={`role-filter-${stat.key}`}
                    onClick={() => setUserRoleFilter(stat.key === 'admin' ? stat.key : stat.key)}
                    className={`p-3 rounded-lg border text-center transition-all ${stat.color} ${
                      userRoleFilter === stat.key ? 'ring-2 ring-offset-1 ring-current font-bold shadow' : 'hover:shadow'
                    }`}
                  >
                    <div className="text-2xl font-bold number-display">{stat.count}</div>
                    <div className="text-xs mt-1">{stat.label}</div>
                  </button>
                ))}
              </div>

              {/* Search */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    data-testid="user-search-input"
                    type="text"
                    placeholder="بحث في المستخدمين (الاسم، البريد، الكنيسة)..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    className="pr-10"
                  />
                </div>
              </div>

              {loadingUsers ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="users-table">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-right font-semibold text-gray-600">#</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-600">المستخدم</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-600">الدور</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-600">الكنيسة</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-600">الحالة</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-600">آخر دخول</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-600">تاريخ الإنشاء</th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-600">إجراءات</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredUsers.map((u, idx) => (
                        <React.Fragment key={u.id}>
                          <tr className={`hover:bg-gray-50 transition-colors ${u.is_active === false ? 'bg-red-50/60' : ''}`}>
                            <td className="px-4 py-3 text-gray-400 number-display">{idx + 1}</td>
                            <td className="px-4 py-3">
                              <div className="font-semibold text-gray-900">{u.full_name || u.username}</div>
                              <div className="text-xs text-gray-500">@{u.username}</div>
                              {u.email && <div className="text-xs text-blue-500">{u.email}</div>}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-block px-2.5 py-1 text-xs rounded-full font-medium ${
                                u.role === 'super_admin' ? 'bg-red-600 text-white' :
                                u.role === 'bishop' ? 'bg-purple-600 text-white' :
                                (u.role === 'admin' || u.role === 'church_admin') ? 'bg-amber-500 text-white' :
                                'bg-green-500 text-white'
                              }`}>
                                {u.role === 'super_admin' ? 'مدير عام' :
                                 u.role === 'bishop' ? 'أسقف' :
                                 (u.role === 'admin' || u.role === 'church_admin') ? 'مدير' : 'مستخدم'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1 text-gray-600">
                                <Building2 className="w-3 h-3 flex-shrink-0" />
                                <span className="text-xs">{u.church_name || 'مركزي'}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {u.is_active === false ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 font-medium">
                                  <X className="w-3 h-3" /> معطّل
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 font-medium">
                                  <CheckCircle className="w-3 h-3" /> نشط
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500" dir="ltr">
                              {u.last_login ? new Date(u.last_login).toLocaleString('ar-EG', { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500" dir="ltr">
                              {u.created_at ? new Date(u.created_at).toLocaleDateString('ar-EG') : '-'}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Button
                                size="sm"
                                variant="ghost"
                                data-testid={`expand-user-${u.id}`}
                                onClick={() => setExpandedUserId(expandedUserId === u.id ? null : u.id)}
                                className="text-gray-500 hover:text-gray-800"
                              >
                                {expandedUserId === u.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </Button>
                            </td>
                          </tr>
                          {/* Expanded Actions Row */}
                          {expandedUserId === u.id && (
                            <tr className="bg-slate-50">
                              <td colSpan={8} className="px-6 py-4">
                                {editingUserRole === u.id ? (
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-medium text-gray-700">تغيير الدور:</span>
                                    <select
                                      value={newRoleValue}
                                      onChange={(e) => setNewRoleValue(e.target.value)}
                                      className="text-sm border rounded px-3 py-1.5"
                                    >
                                      <option value="">اختر الدور</option>
                                      <option value="user">مستخدم</option>
                                      <option value="admin">مدير</option>
                                      <option value="church_admin">مدير كنيسة</option>
                                      <option value="bishop">أسقف</option>
                                    </select>
                                    <Button size="sm" onClick={() => { if (newRoleValue) handleChangeUserRole(u.id, newRoleValue); }} className="bg-green-600 hover:bg-green-700 text-white text-xs">حفظ</Button>
                                    <Button size="sm" variant="outline" onClick={() => { setEditingUserRole(null); setNewRoleValue(''); }} className="text-xs">إلغاء</Button>
                                  </div>
                                ) : changingChurchUser === u.id ? (
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-medium text-gray-700">تغيير الكنيسة:</span>
                                    <select
                                      value={newChurchId}
                                      onChange={(e) => setNewChurchId(e.target.value)}
                                      className="text-sm border rounded px-3 py-1.5"
                                    >
                                      <option value="">اختر الكنيسة</option>
                                      {dashboard?.churches?.map((church) => (
                                        <option key={church.id} value={church.id}>{church.name}</option>
                                      ))}
                                    </select>
                                    <Button size="sm" onClick={() => { if (newChurchId) handleChangeUserChurch(u.id, newChurchId); }} className="bg-blue-600 hover:bg-blue-700 text-white text-xs">حفظ</Button>
                                    <Button size="sm" variant="outline" onClick={() => { setChangingChurchUser(null); setNewChurchId(''); }} className="text-xs">إلغاء</Button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Button
                                      size="sm" variant="outline"
                                      data-testid={`change-role-${u.id}`}
                                      onClick={() => { setEditingUserRole(u.id); setNewRoleValue(u.role); }}
                                      className="text-xs gap-1"
                                      disabled={u.role === 'super_admin'}
                                    >
                                      <UserCog className="w-3 h-3" /> تغيير الدور
                                    </Button>
                                    <Button
                                      size="sm" variant="outline"
                                      data-testid={`change-church-${u.id}`}
                                      onClick={() => { setChangingChurchUser(u.id); setNewChurchId(u.church_id || ''); }}
                                      className="text-xs text-blue-600 border-blue-300 gap-1"
                                      disabled={u.role === 'super_admin' || u.role === 'bishop'}
                                    >
                                      <ArrowLeftRight className="w-3 h-3" /> تغيير الكنيسة
                                    </Button>
                                    <Button
                                      size="sm" variant="outline"
                                      data-testid={`reset-password-${u.id}`}
                                      onClick={() => handleResetUserPassword(u.id)}
                                      className="text-xs gap-1"
                                    >
                                      <Eye className="w-3 h-3" /> إعادة كلمة المرور
                                    </Button>
                                    <Button
                                      size="sm" variant="outline"
                                      data-testid={`toggle-status-${u.id}`}
                                      onClick={() => handleToggleUserStatus(u.id)}
                                      className={`text-xs gap-1 ${u.is_active === false ? 'text-green-600 border-green-300' : 'text-orange-600 border-orange-300'}`}
                                      disabled={u.role === 'super_admin'}
                                    >
                                      {u.is_active === false ? <><CheckCircle className="w-3 h-3" /> تفعيل</> : <><X className="w-3 h-3" /> تعطيل</>}
                                    </Button>
                                    <Button
                                      size="sm" variant="outline"
                                      data-testid={`delete-user-${u.id}`}
                                      onClick={() => handleDeleteAnyUser(u.id)}
                                      className="text-xs text-red-600 border-red-300 hover:bg-red-50 gap-1"
                                      disabled={u.role === 'super_admin'}
                                    >
                                      <Trash2 className="w-3 h-3" /> حذف
                                    </Button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  
                  {filteredUsers.length === 0 && (
                    <div className="text-center text-gray-500 py-8">
                      لا يوجد مستخدمين مطابقين للبحث
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Super Admin - Contact Messages Section */}
        {isSuperAdmin && (
          <Card className="shadow-lg mt-8">
            <CardHeader className="bg-gradient-to-r from-blue-100 to-cyan-100">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-600" />
                  رسائل التواصل
                  {unreadCount > 0 && (
                    <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                      {unreadCount} جديد
                    </span>
                  )}
                </div>
                <Button
                  onClick={fetchContactMessages}
                  disabled={loadingMessages}
                  variant="outline"
                  size="sm"
                >
                  {loadingMessages ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'تحديث'
                  )}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {loadingMessages ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : contactMessages.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>لا توجد رسائل حتى الآن</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {contactMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`p-4 rounded-lg border ${
                        msg.is_read ? 'bg-gray-50 border-gray-200' : 'bg-blue-50 border-blue-300'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-semibold text-gray-900">{msg.name}</span>
                            {!msg.is_read && (
                              <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                                جديد
                              </span>
                            )}
                          </div>
                          
                          {msg.email && (
                            <div className="flex items-center gap-1 text-sm text-gray-600 mb-1">
                              <Mail className="w-3 h-3" />
                              <a href={`mailto:${msg.email}`} className="hover:text-blue-600">
                                {msg.email}
                              </a>
                            </div>
                          )}
                          
                          {msg.phone && (
                            <div className="flex items-center gap-1 text-sm text-gray-600 mb-1">
                              <Phone className="w-3 h-3" />
                              <a href={`tel:${msg.phone}`} className="hover:text-blue-600" dir="ltr">
                                {msg.phone}
                              </a>
                              <a 
                                href={`https://wa.me/${msg.phone.replace(/\D/g, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-green-600 hover:text-green-700 mr-2"
                              >
                                (WhatsApp)
                              </a>
                            </div>
                          )}
                          
                          <div className="mt-3 p-3 bg-white rounded border">
                            <MessageSquare className="w-4 h-4 text-gray-400 mb-1" />
                            <p className="text-gray-700 whitespace-pre-wrap">{msg.message}</p>
                          </div>
                          
                          <p className="text-xs text-gray-400 mt-2">
                            {new Date(msg.created_at).toLocaleString('ar-EG')}
                          </p>
                        </div>
                        
                        <div className="flex flex-col gap-1 mr-4">
                          {!msg.is_read && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleMarkMessageRead(msg.id)}
                              className="text-xs text-green-600 border-green-600"
                            >
                              <CheckCircle className="w-3 h-3 ml-1" />
                              قراءة
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteMessage(msg.id)}
                            className="text-xs text-red-600 border-red-600"
                          >
                            <Trash2 className="w-3 h-3 ml-1" />
                            حذف
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Data Migration Section - Super Admin Only */}
        {isSuperAdmin && (
          <Card data-testid="migration-section" className="shadow-lg mt-8">
            <CardHeader className="bg-gradient-to-r from-indigo-100 to-blue-100">
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5 text-indigo-600" />
                نقل البيانات (Data Migration)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-gray-600 text-sm mb-4">
                استخدم هذه الأدوات لنقل جميع البيانات بين البيئات (المعاينة ← الإنتاج). يشمل ذلك جميع المستخدمين، الشمامسة، التوزيعات، الجداول، الإعدادات، وبيانات الكنائس.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {/* Export */}
                <div className="border-2 border-dashed border-indigo-300 rounded-lg p-6 text-center bg-indigo-50/50">
                  <Download className="w-10 h-10 text-indigo-500 mx-auto mb-3" />
                  <h3 className="font-bold text-indigo-800 mb-2">تصدير البيانات</h3>
                  <p className="text-xs text-gray-500 mb-4">تصدير جميع البيانات كملف JSON للنقل</p>
                  <Button
                    data-testid="export-migration-btn"
                    onClick={handleExportMigration}
                    disabled={migrationExporting}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white w-full"
                  >
                    {migrationExporting ? (
                      <><Loader2 className="w-4 h-4 animate-spin ml-2" /> جاري التصدير...</>
                    ) : (
                      <><Download className="w-4 h-4 ml-2" /> تصدير الكل</>
                    )}
                  </Button>
                </div>

                {/* Import */}
                <div className="border-2 border-dashed border-green-300 rounded-lg p-6 text-center bg-green-50/50">
                  <Upload className="w-10 h-10 text-green-500 mx-auto mb-3" />
                  <h3 className="font-bold text-green-800 mb-2">استيراد البيانات</h3>
                  <p className="text-xs text-gray-500 mb-4">استيراد ملف JSON المُصدّر مسبقاً</p>
                  <label className={`inline-flex items-center justify-center w-full px-4 py-2 rounded-md text-white cursor-pointer transition-colors ${migrationImporting ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}>
                    {migrationImporting ? (
                      <><Loader2 className="w-4 h-4 animate-spin ml-2" /> جاري الاستيراد...</>
                    ) : (
                      <><Upload className="w-4 h-4 ml-2" /> اختيار ملف واستيراد</>
                    )}
                    <input
                      data-testid="import-migration-input"
                      type="file"
                      accept=".json"
                      onChange={handleImportMigration}
                      disabled={migrationImporting}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {/* Migration Results */}
              {migrationResult && (
                <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="font-bold text-green-800 mb-2">نتائج الاستيراد:</h4>
                  <div className="max-h-60 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-green-100">
                        <tr>
                          <th className="px-3 py-1.5 text-right">المجموعة</th>
                          <th className="px-3 py-1.5 text-right">الحالة</th>
                          <th className="px-3 py-1.5 text-right">جديد</th>
                          <th className="px-3 py-1.5 text-right">محدّث</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {Object.entries(migrationResult.results || {}).map(([coll, info]) => (
                          <tr key={coll}>
                            <td className="px-3 py-1.5 font-mono text-xs">{coll}</td>
                            <td className="px-3 py-1.5">
                              <span className={`text-xs px-2 py-0.5 rounded ${info.status === 'success' ? 'bg-green-100 text-green-700' : info.status === 'skipped' ? 'bg-gray-100 text-gray-600' : 'bg-red-100 text-red-700'}`}>
                                {info.status === 'success' ? 'نجح' : info.status === 'skipped' ? 'فارغ' : 'خطأ'}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 number-display">{info.inserted || 0}</td>
                            <td className="px-3 py-1.5 number-display">{info.updated || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Login Logs Section - Super Admin Only */}
        {isSuperAdmin && (
          <Card data-testid="login-logs-section" className="shadow-lg mt-8">
            <CardHeader className="bg-gradient-to-r from-gray-100 to-slate-100">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShieldAlert className="w-5 h-5 text-gray-600" />
                  سجل تسجيل الدخول
                </div>
                <div className="flex gap-2">
                  <select
                    data-testid="login-logs-filter"
                    value={logsFilter}
                    onChange={(e) => { setLogsFilter(e.target.value); fetchLoginLogs(1, e.target.value); }}
                    className="text-sm border rounded px-3 py-1.5 bg-white"
                  >
                    <option value="all">الكل</option>
                    <option value="success">ناجح</option>
                    <option value="failed">فاشل</option>
                  </select>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fetchLoginLogs(logsPage, logsFilter)}
                    className="text-gray-600"
                  >
                    <Clock className="w-4 h-4 ml-1" />
                    تحديث
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="clear-login-logs-btn"
                    onClick={async () => {
                      if (!window.confirm('هل أنت متأكد من مسح جميع سجلات تسجيل الدخول؟')) return;
                      try {
                        const res = await axios.delete(`${API}/superadmin/login-logs`, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        toast.success(res.data.message);
                        setLoginLogs([]);
                      } catch { toast.error('خطأ في مسح السجلات'); }
                    }}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 ml-1" />
                    مسح السجل
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loadingLogs ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : loginLogs.length === 0 ? (
                <p className="text-center py-8 text-gray-500">لا توجد سجلات دخول</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="login-logs-table">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-right font-medium text-gray-600">الحالة</th>
                          <th className="px-4 py-3 text-right font-medium text-gray-600">المستخدم</th>
                          <th className="px-4 py-3 text-right font-medium text-gray-600">الاسم</th>
                          <th className="px-4 py-3 text-right font-medium text-gray-600">الدور</th>
                          <th className="px-4 py-3 text-right font-medium text-gray-600">الكنيسة</th>
                          <th className="px-4 py-3 text-right font-medium text-gray-600">السبب</th>
                          <th className="px-4 py-3 text-right font-medium text-gray-600">التاريخ</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {loginLogs.map((log, idx) => (
                          <tr key={log.id || idx} className={`hover:bg-gray-50 ${!log.success ? 'bg-red-50/50' : ''}`}>
                            <td className="px-4 py-3">
                              {log.success ? (
                                <span className="inline-flex items-center gap-1 text-green-700 bg-green-100 px-2 py-0.5 rounded-full text-xs font-medium">
                                  <CheckCircle className="w-3 h-3" /> ناجح
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-red-700 bg-red-100 px-2 py-0.5 rounded-full text-xs font-medium">
                                  <X className="w-3 h-3" /> فاشل
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 font-medium">{log.username || '-'}</td>
                            <td className="px-4 py-3">{log.full_name || '-'}</td>
                            <td className="px-4 py-3">
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                log.role === 'super_admin' ? 'bg-red-100 text-red-700' :
                                log.role === 'admin' ? 'bg-blue-100 text-blue-700' :
                                log.role === 'bishop' ? 'bg-purple-100 text-purple-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {log.role || '-'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-600">{log.church_name || '-'}</td>
                            <td className="px-4 py-3 text-gray-500 text-xs">{log.reason || '-'}</td>
                            <td className="px-4 py-3 text-gray-500 text-xs" dir="ltr">
                              {log.timestamp ? new Date(log.timestamp).toLocaleString('ar-EG') : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Pagination */}
                  {logsTotalPages > 1 && (
                    <div className="flex items-center justify-center gap-4 py-4 border-t">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => fetchLoginLogs(logsPage - 1, logsFilter)}
                        disabled={logsPage <= 1}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-gray-600">
                        صفحة <span className="number-display">{logsPage}</span> من <span className="number-display">{logsTotalPages}</span>
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => fetchLoginLogs(logsPage + 1, logsFilter)}
                        disabled={logsPage >= logsTotalPages}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Church Details Modal */}
      <ChurchDetailsModal />
    </div>
  );
};

export default BishopDashboard;
